import React from 'react';

import SearchIcon from '@atlaskit/icon/core/search';
import { ChatIcon, ConfluenceIcon, JiraIcon, LoomIcon, StudioIcon } from '@atlaskit/logo';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem } from '@townsquare/analytics-nav4';
import { filterUndefined } from '@townsquare/filter-type';
import { SideNavAppItem } from '@townsquare/griffin-navigation/side-nav-app-item';
import { SideNavItem } from '@townsquare/griffin-navigation/types';
import { useFilterProductLinks } from '@townsquare/griffin-navigation/use-filter-product-links';
import {
	SideNavAppItem as SideNavAppItemID,
	SideNavProductItem,
	SideNavItemType,
} from '@townsquare/navigation-config/nav4';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import { chatRoute } from '@townsquare/routes/chat';
import { createAgentRoute } from '@townsquare/routes/create-agent';
import { editAgentRoute } from '@townsquare/routes/edit-agent';
import { rovoSearchRoute } from '@townsquare/routes/rovo-search-view';

import { ExternalSideNavItem } from './ExternalSideNavItem';

const itemsThatBypassProductFilter: SideNavItemType[] = [
	SideNavProductItem.CHAT,
	SideNavProductItem.SEARCH,
];

/**
 * Generates the full list of possible products to be shown in the left navigation in the new Rovo Search refresh experience.
 */
export const useSearchSideNavItems = (): SideNavItem[] => {
	const intl = useIntl();
	const routeDependencies = useRouteDependencies();
	const filterProductLinks = useFilterProductLinks();

	return [
		{
			id: SideNavProductItem.SEARCH,
			analyticsSubject: AnalyticsSideNavItem.SEARCH,
			path: generatePath(rovoSearchRoute, routeDependencies),
			matches: [rovoSearchRoute],
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.rovosearch-side-nav.new-search',
				description: 'Side nav button to link to the fullscreen Rovo search view',
				defaultMessage: 'New search',
			}),
			testId: 'rovosearch-side-nav-new-search',
			Icon: SearchIcon,
		},
		{
			id: SideNavAppItemID.JIRA,
			analyticsSubject: AnalyticsSideNavItem.JIRA,
			path: '', // this will be patched up by <SearchSidebarGSNWrapper /> before being rendered by the GSN
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.rovosearch-side-nav.jira',
				description: 'Side nav button to link to the Jira application',
				defaultMessage: 'Jira',
			}),
			testId: 'rovosearch-side-nav-jira',
			Icon: () => <JiraIcon size="xsmall" shouldUseNewLogoDesign />,
			Component: SideNavAppItem,
		},
		{
			id: SideNavAppItemID.CONFLUENCE,
			analyticsSubject: AnalyticsSideNavItem.CONFLUENCE,
			path: '', // this will be patched up by <SearchSidebarGSNWrapper /> before being rendered by the GSN
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.rovosearch-side-nav.confluence',
				description: 'Side nav button to link to the Confluence application',
				defaultMessage: 'Confluence',
			}),
			testId: 'rovosearch-side-nav-confluence',
			Icon: () => <ConfluenceIcon size="xsmall" shouldUseNewLogoDesign />,
			Component: SideNavAppItem,
		},
		{
			id: SideNavAppItemID.LOOM,
			analyticsSubject: AnalyticsSideNavItem.LOOM,
			path: '', // this will be patched up by <SearchSidebarGSNWrapper /> before being rendered by the GSN
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.rovosearch-side-nav.loom',
				description: 'Side nav button to link to the Loom application',
				defaultMessage: 'Loom',
			}),
			testId: 'rovosearch-side-nav-loom',
			Icon: () => <LoomIcon size="xsmall" shouldUseNewLogoDesign />,
			Component: SideNavAppItem,
		},
		{
			id: SideNavProductItem.CHAT,
			analyticsSubject: AnalyticsSideNavItem.CHAT,
			path: generatePath(chatRoute, routeDependencies),
			matches: [chatRoute, createAgentRoute, editAgentRoute],
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.rovosearch-side-nav.chat',
				description: 'Side nav button to open full page AI chat',
				defaultMessage: 'Chat',
			}),
			testId: 'rovosearch-side-nav-rovo-chat',
			Icon: () => <ChatIcon size="xsmall" shouldUseNewLogoDesign />,
			Component: ExternalSideNavItem,
		},
		{
			id: SideNavAppItemID.STUDIO,
			analyticsSubject: AnalyticsSideNavItem.STUDIO,
			path: '', // this will be patched up by <SearchSidebarGSNWrapper /> before being rendered by the GSN
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.rovosearch-side-nav.studio',
				description: 'Side nav button to link to the Studio application',
				defaultMessage: 'Studio',
			}),
			testId: 'rovosearch-side-nav-studio',
			Icon: () => <StudioIcon size="xsmall" shouldUseNewLogoDesign />,
			Component: SideNavAppItem,
		},
	]
		.filter(filterUndefined)
		.filter((item) => {
			// There are some menu items in this list that have URL paths already populated
			// based on a set route, and do not need to be dynamically generated. We want to
			// bypass the below filter for these apps or else they will be incorrectly filtered out.
			if (itemsThatBypassProductFilter.includes(item.id)) {
				return true;
			}
			return filterProductLinks(item);
		});
};
